import { Check, X } from "lucide-react";
import { cn } from "lib/utils";
import type { OutreachPath, PathStep } from "../api/OutreachTouchesApi";

const STEPS: { key: keyof OutreachPath["steps"]; label: string }[] = [
  { key: "request", label: "Request" },
  { key: "accepted", label: "Accepted" },
  { key: "message", label: "Message" },
  { key: "follow_up", label: "Follow-up" },
  { key: "replied", label: "Replied" },
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function shortDay(value: string | null): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? "");
  return m ? `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}` : "";
}

/** The five steps of one path: done (green tick and day), the next one (ringed), pending (hollow), dropped (struck, grey). */
export default function PathStepper({ path }: { path: OutreachPath }) {
  const ended = path.state === "replied" || path.state === "closed" || path.state === "skipped";
  const nextKey = ended ? null : STEPS.find((s) => path.steps[s.key].status === "pending")?.key ?? null;
  return (
    <ol className="flex items-start" aria-label="Progress">
      {STEPS.map((s, i) => {
        const step: PathStep = path.steps[s.key];
        const next = s.key === nextKey;
        return (
          <li key={s.key} className="flex items-start" aria-current={next ? "step" : undefined}>
            <div className="flex flex-col items-center w-[58px]">
              <span
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded-full border text-[10px]",
                  step.status === "done" && "border-success bg-success text-success-foreground",
                  step.status === "dropped" && "border-border bg-surface-muted text-muted-foreground",
                  step.status === "pending" && (next ? "border-primary ring-2 ring-primary/30 bg-surface" : "border-border bg-surface"),
                )}
                data-status={step.status}
                data-testid={`step-${s.key}`}
              >
                {step.status === "done" && <Check className="h-3 w-3" aria-hidden />}
                {step.status === "dropped" && <X className="h-3 w-3" aria-hidden />}
              </span>
              <span className={cn("mt-1 text-[11px] leading-none", step.status === "dropped" ? "text-muted-foreground/60 line-through" : next ? "font-semibold text-primary" : "text-muted-foreground")}>{s.label}</span>
              <span className="mt-0.5 h-3 text-[10px] leading-none text-muted-foreground">{step.status === "done" ? shortDay(step.on) : ""}</span>
            </div>
            {i < STEPS.length - 1 && <span aria-hidden className={cn("mt-2.5 h-px w-3", step.status === "done" ? "bg-success" : "bg-border")} />}
          </li>
        );
      })}
    </ol>
  );
}
